import { useEffect, useMemo, useState } from 'react'
import { useLocation } from 'react-router-dom'
import { useQuery } from 'hooks'
import { DataTable, DataTablePagination } from 'components/common'
import { getColumns } from './utils'
import { fetchAggregators } from 'utils/api/aggregatorApi'
import ConfirmDeleteAggregatorModal from './confirmDeleteAggregatorModal'
import { ICellRendererParams } from '@ag-grid-community/core'
import useModal from 'hooks/useModal'

type AggregatorsTableProps = {
  isEditor: boolean
}

const AggregatorsTable = ({ isEditor }: AggregatorsTableProps) => {
  const [aggregatorsData, setAggregatorsData] = useState<any>({})
  const [loading, setLoading] = useState<boolean>(false)

  // preserve the selected aggregator params while open modal for deletion
  const [gridParams, setGridParams] = useState<ICellRendererParams>()

  const { search } = useLocation()
  const { getAllQueries } = useQuery()
  const { closeModal, isOpen, openModal } = useModal()

  const getAggregators = async () => {
    setLoading(true)

    const { limit, search: SearchTerm, page } = getAllQueries()
    try {
      const { data } = await fetchAggregators({
        limit: limit || 10,
        page: page || 1,
        search: SearchTerm,
      })
      setAggregatorsData({ ...data })
    } catch (error) {
      setAggregatorsData({ docs: [] })
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    getAggregators()
  }, [search])

  const getAggregatorToDelete = (params: ICellRendererParams) => {
    if (!isEditor) return
    setGridParams(params)
    openModal()
  }

  const columns = useMemo(
    () => getColumns(isEditor, getAggregatorToDelete),
    [isEditor],
  )

  return (
    <>
      <DataTable
        columns={columns}
        rows={loading ? null : aggregatorsData?.docs}
        identifier="_id"
      />

      {/* Confirm Delete Modal */}
      {isEditor && (
        <ConfirmDeleteAggregatorModal
          params={gridParams}
          closeModal={closeModal}
          isOpen={isOpen}
        />
      )}

      {/* Pagination */}
      <DataTablePagination
        currentPage={aggregatorsData?.page}
        totalPages={aggregatorsData?.totalPages}
        hasNextPage={aggregatorsData?.hasNextPage}
        hasPrevPage={aggregatorsData?.hasPrevPage}
        limit={aggregatorsData?.limit}
        nextPage={aggregatorsData?.nextPage}
        prevPage={aggregatorsData?.prevPage}
      />
    </>
  )
}

export default AggregatorsTable
